import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { RootState } from "../store";

// All available agencies in the public lands data
export const AGENCIES = [
  "BLM",
  "DOD",
  "FWS",
  "NPS",
  "SPR",
  "USACE",
  "USFS",
] as const;
export type Agency = (typeof AGENCIES)[number];

// Map of agency codes to full names
export const AGENCY_NAMES: Record<Agency, string> = {
  BLM: "Bureau of Land Management",
  DOD: "Department of Defense",
  FWS: "Fish & Wildlife Service",
  NPS: "National Park Service",
  SPR: "State Parks",
  USACE: "US Army Corps of Engineers",
  USFS: "US Forest Service",
};

// Map of agency codes to their display colors (matching the layer colors)
export const AGENCY_COLORS: Record<Agency, string> = {
  USFS: "#2bc52bff",
  BLM: "#fffb00ff",
  NPS: "#ea9d10ff",
  FWS: "#4a7c7c",
  DOD: "#d7214eff",
  SPR: "#87ceeb",
  USACE: "#d369ddff"
};

type PublicLandsLayerState = {
  visible: boolean;
  selectedAgencies: Agency[];
};

const initialState: PublicLandsLayerState = {
  visible: false,
  selectedAgencies: [...AGENCIES], // All agencies selected by default
};

const publicLandsLayer = createSlice({
  name: "publicLandsLayer",
  initialState,
  reducers: {
    setPublicLandsVisible: (state, action: PayloadAction<boolean>) => {
      state.visible = action.payload;
    },
    togglePublicLandsVisible: (state) => {
      state.visible = !state.visible;
    },
    setSelectedAgencies: (state, action: PayloadAction<Agency[]>) => {
      state.selectedAgencies = action.payload;
    },
    toggleAgency: (state, action: PayloadAction<Agency>) => {
      const agency = action.payload;
      const index = state.selectedAgencies.indexOf(agency);

      if (index >= 0) {
        // Remove agency if it exists
        state.selectedAgencies = state.selectedAgencies.filter(
          (a) => a !== agency
        );
      } else {
        // Add agency if it doesn't exist
        state.selectedAgencies = [...state.selectedAgencies, agency];
      }
    },
  },
});

export const {
  setPublicLandsVisible,
  togglePublicLandsVisible,
  setSelectedAgencies,
  toggleAgency,
} = publicLandsLayer.actions;

export default publicLandsLayer.reducer;

// Selectors
const selectPublicLandsVisible = (state: RootState) =>
  state.publicLandsLayer.visible;

const selectSelectedAgencies = (state: RootState) =>
  state.publicLandsLayer.selectedAgencies;

export const usePublicLandsVisible = () => {
  return useSelector(selectPublicLandsVisible);
};

export const useSelectedAgencies = () => {
  return useSelector(selectSelectedAgencies);
};
